import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { airportByLowerCode } from "@/lib/seo-routes";
import { findAirport } from "@/lib/airports";
import { formatDisplayPrice } from "@/lib/price-display";
import { fetchRouteStats, pairKey } from "@/lib/price-stats.functions";
import { classifyPrice, type PriceBadge } from "@/lib/price-badge";

export type Deal = {
  from: string;
  to: string;
  city: string;
  country: string;
  price: string;
  priceNumeric: number;
  /** Data lotu (YYYY-MM-DD), dla której obowiązuje cena z Aviasales. */
  departDate?: string;
  badge?: PriceBadge | null;
};

const InputSchema = z.object({
  from: z.string().min(3).max(3).regex(/^[A-Z]+$/),
  limit: z.number().int().min(1).max(24).optional().default(8),
});

/**
 * Najtańsze loty z danego miasta wylotu — czytane z cache `route_prices`.
 * Rozszerza kody metro (WAR → WAW+WMI, LON → LHR+LGW+STN+LTN+LCY) i
 * deduplikuje po mieście docelowym (jedno lotnisko per miasto, najtaniej).
 */
export const getDealsFromOrigin = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => InputSchema.parse(input))
  .handler(async ({ data }): Promise<Deal[]> => {
    try {
      const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
      const key =
        process.env.SUPABASE_SERVICE_ROLE_KEY ??
        process.env.SUPABASE_PUBLISHABLE_KEY ??
        process.env.SUPABASE_ANON_KEY ??
        process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      if (!url || !key) return [];

      const sb = createClient(url, key, {
        auth: { persistSession: false, autoRefreshToken: false },
      });

      const fromAp = airportByLowerCode(data.from.toLowerCase());
      const fromCodes = fromAp?.isMetro && fromAp.members?.length
        ? fromAp.members
        : [data.from.toUpperCase()];

      const { data: rows } = await sb
        .from("route_prices")
        .select("from_iata,to_iata,display_price,depart_date")
        .in("from_iata", fromCodes)
        .order("display_price", { ascending: true })
        .limit(200);
      if (!rows || rows.length === 0) return [];

      const bestPerCity = new Map<string, Deal>();
      for (const r of rows) {
        if (typeof r.display_price !== "number" || r.display_price <= 0) continue;
        const at = findAirport(r.to_iata);
        if (!at) continue;
        const key = `${at.city}|${at.country}`;
        const existing = bestPerCity.get(key);
        if (existing && existing.priceNumeric <= r.display_price) continue;
        bestPerCity.set(key, {
          from: r.from_iata,
          to: r.to_iata,
          city: at.city,
          country: at.country,
          priceNumeric: r.display_price,
          departDate: (r as { depart_date?: string | null }).depart_date ?? undefined,
          price: formatDisplayPrice(r.display_price),
        });
      }

      const deals = [...bestPerCity.values()]
        .sort((a, b) => a.priceNumeric - b.priceNumeric)
        .slice(0, data.limit);

      // Wzbogać o badge ("Najtaniej od 30 dni", "X% taniej niż zwykle"…).
      // Jedno batched zapytanie do route_price_history dla wszystkich par.
      try {
        const stats = await fetchRouteStats(
          deals.map((d) => ({ from: d.from, to: d.to })),
          30,
        );
        for (const d of deals) {
          d.badge = classifyPrice(d.priceNumeric, stats[pairKey(d.from, d.to)]);
        }
      } catch {
        // Brak badge'a to nie błąd — feed pokazuje się normalnie.
      }

      return deals;
    } catch {
      return [];
    }
  });
